import { type FC } from 'react'

import { Box, Button, Table, Tag, Text, useToggle } from '@factorialco/deprecated-design-system'
import {
  SalaryRangesTableRowLegalEntitiesFragment,
  SalaryRangesTableRowRoleFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'

import DeleteSalaryRangeModal from 'modules/job_catalog/components/DeleteSalaryRangeModal'
import { formatSalaryFromCents } from 'modules/job_catalog/lib/salaryUtils'

interface Props {
  salaryRange: SalaryRangesTableRowRoleFragment['levels'][number]['salaryRanges'][number]
  legalEntities?: SalaryRangesTableRowLegalEntitiesFragment[]
  onEdit: () => void
  onDeleted: () => void
}

const Row: FC<Props> = ({ salaryRange, legalEntities, onEdit, onDeleted }) => {
  const jobLevel = salaryRange.level?.name
  const { minRange, maxRange, currency, salaryFrequency, amountType } = salaryRange
  const [isDeleteModalOpen, toggleModalOpen] = useToggle(false)

  const minAmount = formatSalaryFromCents(minRange, undefined, currency)
  const maxAmount = formatSalaryFromCents(maxRange, undefined, currency)
  const allEntities = legalEntities?.length === salaryRange.legalEntities.length

  const handleDelete = () => {
    toggleModalOpen(true)()
  }

  const handleEdit = () => {
    onEdit()
  }

  const handleDeleted = () => {
    toggleModalOpen(false)()
    onDeleted()
  }

  return (
    <>
      <Table.Row>
        <Table.Cell>
          <Text.Normal weight="medium">{jobLevel}</Text.Normal>
        </Table.Cell>
        <Table.Cell colSpan={2}>
          {salaryRange.legalEntities.length && (
            <Box flexDirection="row" gap="s8">
              {allEntities ? (
                <Tag type="champagne">{i18n.t('job_catalog.salary_ranges.all_entities')}</Tag>
              ) : (
                <Tag type="champagne">{salaryRange.legalEntities[0]?.legalName}</Tag>
              )}

              {salaryRange.legalEntities.length > 1 && !allEntities && (
                <Box flexNoShrink>
                  <Tag type="grey">+{salaryRange.legalEntities.length - 1}</Tag>
                </Box>
              )}
            </Box>
          )}
        </Table.Cell>
        <Table.Cell>
          <Text.Normal>{salaryFrequency}</Text.Normal>
        </Table.Cell>
        <Table.Cell colSpan={2}>
          <Text.Normal ellipsis>
            {amountType === 'range' && `${minAmount} - `}
            {`${maxAmount} `}
          </Text.Normal>
        </Table.Cell>
        <Table.Cell>
          <Box flexDirection="row" justifyContent="flexEnd">
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="center">
                <PopoverMenu.TouchableOption
                  title={i18n.t('job_catalog.salary_ranges.edit_salary_range')}
                  onClick={handleEdit}
                />
                <PopoverMenu.TouchableOption
                  title={i18n.t('job_catalog.salary_ranges.remove_salary_range')}
                  onClick={handleDelete}
                  danger
                />
              </PopoverMenu.Content>
            </PopoverMenu>
          </Box>
        </Table.Cell>
      </Table.Row>
      {isDeleteModalOpen && (
        <DeleteSalaryRangeModal
          id={salaryRange.id}
          onClose={toggleModalOpen(false)}
          onDeleted={handleDeleted}
        />
      )}
    </>
  )
}

export default observer(Row)
